/**
 * Row virtualization for the v8 {@link DataTable}.
 *
 * Windows the table body with `@tanstack/react-virtual`, following the official TanStack v8
 * virtualized-rows recipe (position-relative tbody + per-row `transform: translateY` + dynamic
 * `measureElement` heights). It returns the virtualizer instance, a `measureRowRef` for the
 * windowed rows and the row estimate in use; the scroll-restoration wiring lives in {@link DataTable} (via
 * {@link useScrollRestoration}), not in this hook.
 *
 * ==== Why @tanstack/react-virtual (not react-window / react-virtuoso) ====
 *  - It is the SAME family as the v8 table engine (`@tanstack/react-table`) and shares its
 *    measurement model: the virtualizer is driven by a `count` + an `estimateSize`, emits
 *    `VirtualItem`s whose `index` maps straight back into `table.getRowModel().rows`, and measures
 *    real DOM heights via a single `measureElement` ref callback. There is no separate item-data
 *    plumbing (react-window's `itemData` + a wrapper row component) and no fixed list height: the
 *    container is the scroll element, so the header/totals can stay PINNED (sticky) outside the
 *    window in the same scroller. react-window forces an inner fixed-height list that the sticky
 *    header cannot escape (the documented sticky-header bug), and react-virtuoso owns its own
 *    scroller + DOM, which would fight our container-scoped `--utw-*` CSS-var geometry and the
 *    resize/reorder pipeline. react-virtual gives us the windowing while leaving the table DOM and
 *    the geometry contract entirely ours.
 *  - Variable heights (wrapping cells, edge bars, expanded sub-rows) are first-class via
 *    `measureElement` — no per-row ResizeObserver fan-out, which is what a fixed-height list needs
 *    in order to report a variable row height at all.
 *
 * ==== Load-more anchoring ====
 *  Load-more appends rows at the end, growing the total size without moving the offset —
 *  react-virtual's default `anchorTo: 'start'` keeps the viewport anchored to the top of the
 *  rendered window, so appended rows do not shift what the user is looking at.
 */
import { measureElement as measureByDefault, useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Seed height before ANY row has been measured. Tuned to the common single-line invoice row (16px
 *  cell padding top+bottom + ~24px line box). It is only the starting point: the estimate re-tunes
 *  itself to what the rows actually measure (see {@link nextRowEstimate}), because a seed that is
 *  wrong for a given table stays wrong for every row the window has not reached yet. */
export const DEFAULT_ROW_ESTIMATE = 56;

/** Measured rows required before the estimate re-tunes, so one unusually tall row cannot swing it. */
export const MIN_ESTIMATE_SAMPLES = 5;
/** How far the measured average must diverge from the estimate in use before it is worth re-tuning.
 *  Re-tuning re-flows every UNMEASURED row, so the bar is set well above measurement noise. */
export const ESTIMATE_RETUNE_RATIO = 0.15;

/**
 * The estimate to switch to given what the rendered rows actually measure, or `null` to keep the
 * current one.
 *
 * WHY THIS EXISTS. `estimateSize` is what the virtualizer uses for every row it has not rendered
 * yet, so it sets the height of the spacer below the window — and therefore where the end of the
 * list is. A fixed seed is only ever right for the table it was tuned on: at 56px against rows that
 * really are ~126px, a 100-row list reports a ~7000px scroll height instead of ~12600px, so the
 * scrollbar lies, the scroll position drifts as rows measure, and anything anchored to the end of
 * the content (the infinite-scroll sentinel) sits far above the real end. Averaging the rows the
 * window HAS measured costs nothing — they are measured either way — and converges after the first
 * screenful.
 *
 * Re-tuning only moves rows the user has not seen: rows already measured keep their measured size,
 * and on a downward scroll everything above the viewport is measured, so the re-flow happens below
 * the fold.
 */
export const nextRowEstimate = (current: number, measuredAverage: number, sampleCount: number): number | null => {
    if (sampleCount < MIN_ESTIMATE_SAMPLES || measuredAverage <= 0 || current <= 0) return null;
    const rounded = Math.round(measuredAverage);
    if (rounded === current) return null;
    return Math.abs(rounded - current) / current > ESTIMATE_RETUNE_RATIO ? rounded : null;
};

/**
 * Drops measurements for rows the list no longer has, returning the remaining total.
 *
 * Heights are recorded per row INDEX, which is what the measured node carries. That is fine while the
 * list only grows, but a filter or search that replaces a tall result set with a shorter one leaves
 * the samples for every index past the new end in place — so the average, and therefore the estimate
 * and the virtual height, would be tuned against rows that are gone.
 */
export const pruneMeasuredSizes = (sizes: Map<number, number>, rowCount: number): number => {
    let sum = 0;
    for (const [index, size] of sizes) {
        // Deleting the current key mid-iteration is safe for a Map (it is simply not revisited).
        if (index >= rowCount) sizes.delete(index);
        else sum += size;
    }
    return sum;
};

/** Overscan: rows rendered just outside the viewport so a fast scroll never shows a blank gap.
 *  Five rows covers a fast wheel / trackpad flick at the row heights these lists render. */
const OVERSCAN = 5;

/**
 * The memo key the {@link DataTable} body re-renders on. It encodes the rendered window (first
 * visible index + count) and the total spacer size, so the body re-renders EXACTLY when the window
 * moves, the row count changes (load-more), or a measured height shifts the spacer — and not on an
 * unrelated parent re-render. Pure for testability; an empty window has no first index (-1).
 */
export const virtualEpochToken = (firstIndex: number | undefined, visibleCount: number, totalSize: number): string =>
    `${firstIndex ?? -1}:${visibleCount}:${totalSize}`;

type UseDataTableRowVirtualizerProps = {
    /** Number of top-level rows (== `table.getRowModel().rows.length`). */
    rowCount: number;
    /** The vertical scroll element (the DataTable scroll container). */
    getScrollElement: () => HTMLElement | null;
    /** Per-row estimate before measurement — the SEED. The hook re-tunes from there once enough
     *  rows have been measured. */
    estimateRowHeight: number;
    /** Stable per-row key so measurement caches survive sort / load-more reorders: heights are
     *  cached by row id, not index, so a row that moves keeps the height it was measured at. */
    getItemKey: (index: number) => string | number;
    /** Where the rows start in the scroll element's content, for a list that does not begin at its top. */
    scrollMargin?: number;
};

const IS_FIREFOX = typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') !== -1;

/**
 * Build the row virtualizer + the measure ref attached to every windowed row.
 *
 * `measureElement` uses `getBoundingClientRect().height` (skipped on Firefox per the official v8
 * example, where rAF-batched RO measurement is smoother) so wrapping rows + edge bars + expanded
 * sub-rows all measure their real height. The returned `measureRowRef` is attached to every
 * windowed <tr>; `getVirtualItems()` then drives which rows render and their `start` offset.
 */
export const useDataTableRowVirtualizer = ({
    rowCount,
    getScrollElement,
    estimateRowHeight,
    getItemKey,
    scrollMargin,
}: UseDataTableRowVirtualizerProps): {
    virtualizer: Virtualizer<HTMLElement, HTMLTableRowElement>;
    measureRowRef: (node: HTMLTableRowElement | null) => void;
    /** The estimate in use: the seed until the measured rows re-tune it (see {@link nextRowEstimate}). */
    rowEstimate: number;
} => {
    // The estimate actually in use: seeded by the caller, then re-tuned to the measured average.
    // Mirrored in a ref so the measure callback can read + advance it without taking it as a dep
    // (the callback must stay referentially stable — it is a memoized-body prop).
    const [rowEstimate, setRowEstimate] = useState(estimateRowHeight);
    const rowEstimateRef = useRef(estimateRowHeight);
    // Measured heights by row index. Keyed (rather than a running sum) so a row that re-measures
    // replaces its own contribution instead of being counted twice.
    const measuredRef = useRef<{ sizes: Map<number, number>; sum: number }>({ sizes: new Map(), sum: 0 });

    const estimateSize = useCallback(() => rowEstimate, [rowEstimate]);

    // Every height the virtualizer takes also feeds the running average behind the self-tuning estimate.
    // Recorded where the virtualizer measures (on mount while the list is still, from its resize
    // observer once layout is done while it scrolls), never off the row ref: a read there forces a
    // layout in every commit that brings rows in during a scroll, which the virtualizer itself avoids.
    // Re-measuring a row to the SAME height exits before touching state, which is what stops a
    // measure -> re-estimate -> re-measure loop (a new estimate only moves unmeasured rows, so the
    // measured ones come back identical).
    const recordRowHeight = useCallback((row: HTMLTableRowElement, height: number) => {
        const index = Number(row.getAttribute('data-index'));
        if (!Number.isInteger(index) || height <= 0) return;
        const measured = measuredRef.current;
        const previous = measured.sizes.get(index);
        if (previous === height) return;
        measured.sizes.set(index, height);
        measured.sum += height - (previous ?? 0);
        const next = nextRowEstimate(rowEstimateRef.current, measured.sum / measured.sizes.size, measured.sizes.size);
        if (next !== null) {
            rowEstimateRef.current = next;
            setRowEstimate(next);
        }
    }, []);

    // Forget rows the list no longer has, so the average behind the estimate only ever reflects rows
    // that are currently present (a filter narrowing a tall result set would otherwise keep tuning
    // against the heights it measured before).
    useEffect(() => {
        const measured = measuredRef.current;
        if (measured.sizes.size === 0) return;
        measured.sum = pruneMeasuredSizes(measured.sizes, rowCount);
    }, [rowCount]);

    // react-hooks-js/incompatible-library: like TanStack Table's `useReactTable`, `useVirtualizer`
    // returns an instance whose methods are functions the React Compiler cannot memoize, so it
    // skips compiling this hook. This is inherent to the TanStack API (not refactorable) — the same
    // legitimate-disable category as DataTable's `useReactTable` disable. The performance contract
    // does NOT rely on the compiler here: the consumer hand-memoizes the body and keys it on a
    // derived `virtualEpoch` token, and the returned `measureRowRef` is a stable useCallback.
    // eslint-disable-next-line react-hooks-js/incompatible-library -- TanStack Virtual returns non-memoizable functions; manual memoization is load-bearing here
    const virtualizer = useVirtualizer<HTMLElement, HTMLTableRowElement>({
        count: rowCount,
        getScrollElement,
        estimateSize,
        overscan: OVERSCAN,
        getItemKey,
        scrollMargin,
        // Dynamic measurement for variable row heights. The official v8 recipe reads
        // getBoundingClientRect().height directly off the rendered <tr> and skips Firefox (where
        // the default rAF-batched ResizeObserver path is smoother). A <tr> in our virtualized
        // grid/flex body has a real box, so its rect height is the true row height (incl. an
        // expanded sub-row, which is its own measured windowed row).
        measureElement: (element, entry, instance) => {
            const height = IS_FIREFOX
                ? measureByDefault(element, entry, instance)
                : element.getBoundingClientRect().height;
            recordRowHeight(element, height);
            return height;
        },
    });

    // Stable ref forwarded onto every windowed <tr>. `virtualizer.measureElement` already reads
    // `data-index` off the node, so a single shared callback measures whichever row mounts. The
    // virtualizer instance is referentially stable across renders (TanStack memoizes it), so this
    // callback is stable too — keeping the memoized TableBody from churning on its identity.
    const measureRowRef = useCallback(
        (node: HTMLTableRowElement | null) => virtualizer.measureElement(node),
        [virtualizer]
    );

    // The instance, the row ref and the estimate. The consumer reads the live window (getVirtualItems)
    // + spacer height (getTotalSize) THROUGH `virtualizer` each render — react-virtual triggers the
    // re-render on scroll/measure, and DataTable derives a `virtualEpoch` token from those so its
    // memoized body re-renders exactly when the window moves. No object memo needed: the instance and
    // the ref are stable, and the estimate is a number.
    return { virtualizer, measureRowRef, rowEstimate };
};
